import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { firstValueFrom } from 'rxjs';

import { useResourceBookingServiceClient } from 'hooks/use-resource-booking-service-client.hook';
import { DEFAULT_DATE_FORMAT } from 'pages/resource/constants';
import { CoworkerAttendanceDayResponse, CoworkerAttendanceResponse } from 'store/api-client/coworker-types';

// Bookings move through the day; a minute is the same freshness the coworker search accepts.
const STALE_TIME = 60 * 1000;

export const COWORKER_ATTENDANCE_QUERY_KEY = 'coworker-attendance';

/** The Monday (ISO week start) of the week a `YYYY-MM-DD` day falls in — what the BE keys a week by. */
export const weekStartOf = (date: string): string =>
  moment(date, DEFAULT_DATE_FORMAT, true).isoWeekday(1).format(DEFAULT_DATE_FORMAT);

interface UseCoworkerAttendanceQueryParams {
  /** Any day of the wanted week, YYYY-MM-DD; the query asks for that week's Monday–Friday. */
  date: string;
  /** A group the requester owns, or null for the whole visible directory. */
  groupUuid: string | null;
  /** `useCoworkerVisibility().effectiveTeams` — the endpoint is behind the teams toggle. */
  enabled?: boolean;
}

export interface UseCoworkerAttendanceQueryResult {
  attendance: CoworkerAttendanceResponse | null;
  /** The requested day's entry, or null when the day is outside the Monday–Friday window (a weekend). */
  day: CoworkerAttendanceDayResponse | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Who of the requester's coworkers (or of one group) is in the building each weekday of a week — the
 * "Plan your week" data, and the focus-mode banner's "N of M booked today" (§C3), where N is the
 * day's `present` and M the scope's `totalInScope`.
 */
export const useCoworkerAttendanceQuery = ({
  date,
  groupUuid,
  enabled = true,
}: UseCoworkerAttendanceQueryParams): UseCoworkerAttendanceQueryResult => {
  const { getClient, buildingUuid } = useResourceBookingServiceClient();

  const weekStart = weekStartOf(date);
  const isEnabled = enabled && !!buildingUuid;

  const result = useQuery({
    queryKey: [COWORKER_ATTENDANCE_QUERY_KEY, buildingUuid, weekStart, groupUuid ?? 'all'],
    queryFn: () =>
      firstValueFrom(
        getClient().getCoworkerAttendance(
          buildingUuid as string,
          groupUuid ? { weekStart, groupUuid } : { weekStart, scope: 'all' },
        ),
      ),
    enabled: isEnabled,
    staleTime: STALE_TIME,
  });

  const attendance = result.data ?? null;

  return {
    attendance,
    day: attendance?.days.find(entry => entry.date === date) ?? null,
    isLoading: isEnabled && result.isPending,
    isError: result.isError,
  };
};
